import {
  ACCESS_TOKEN_KEY,
  AUTH,
  CLOSE_BONUS_ALERT,
  CURRENCIE,
  CURRENCIE_USER,
  CURRENT_GROUP,
  REQUEST_LOGIN,
  USER_KEY,
} from '@kimbocare/core';

/**
 * localStorage key holding the tenant's allowed countries, persisted at login
 * as a JSON-encoded `ICountryModel[]`.
 */
export const TENANT_COUNTRIES_KEY = 'tenant_countries';

/** localStorage key holding the UUID of the user's current tenant. */
export const TENANT_UUID_KEY = 'tenant_uuid';

/** Returns the current tenant UUID from localStorage, or `null` if absent. */
export const getTenantUuid = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  return localStorage.getItem(TENANT_UUID_KEY);
};

/**
 * Keys in `localStorage` that must be transferred across subdomains for authentication
 * and user session state to be preserved.
 */
const TRANSFER_KEYS = [
  ACCESS_TOKEN_KEY,
  CLOSE_BONUS_ALERT,
  CURRENCIE,
  CURRENCIE_USER,
  CURRENT_GROUP,
  REQUEST_LOGIN,
  USER_KEY,
  AUTH,
  'tenant_uuid',
  TENANT_COUNTRIES_KEY,
];

/**
 * Reads required authentication keys from local storage, bundles them into a JSON string,
 * and encodes them into Base64 for safe URL transport.
 *
 * @returns A base64 string representing the current authentication state.
 */
export const exportAuthStateForTransfer = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }

  const stateObj: Record<string, string | null> = {};

  TRANSFER_KEYS.forEach((key) => {
    stateObj[key] = localStorage.getItem(key);
  });

  // Convert object to JSON string
  const jsonString = JSON.stringify(stateObj);

  // Encode to Base64 (btoa). Using encodeURIComponent to handle special characters safely in URLs
  return btoa(encodeURIComponent(jsonString));
};

/**
 * Decodes a base64 string containing authentication state, parses it as JSON,
 * and stores each key-value pair back into the local `localStorage`.
 *
 * @param base64State The base64 encoded string from the URL parameter.
 * @returns boolean True if successful, false if decoding or parsing failed.
 */
export const importAuthStateFromTransfer = (base64State: string): boolean => {
  if (typeof window === 'undefined' || !base64State) {
    return false;
  }

  try {
    // Decode Base64 and URI component
    const jsonString = decodeURIComponent(atob(base64State));
    const stateObj: Record<string, string | null> = JSON.parse(jsonString);

    if (stateObj) {
      Object.keys(stateObj).forEach((key) => {
        const itemValue = stateObj[key];
        if (itemValue !== null && itemValue !== undefined) {
          localStorage.setItem(key, itemValue);
        } else {
          localStorage.removeItem(key);
        }
      });
      return true;
    }
  } catch (error) {
    console.error('Failed to import auth state payload', error);
  }

  return false;
};
